import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  describeCall,
  formatDateTime,
  formatPhone,
  type Call,
} from '@fsm/shared';
import { api } from '../lib/api';
import { useCalls } from '../lib/calls';
import { CallActions, useTelephony } from '../components/CallActions';
import { PageFooter } from '../components/Layout';
import { Icon } from '../components/icons';
import {
  Badge,
  Button,
  Card,
  cx,
  EmptyState,
  FilterTabs,
  Input,
  PageHeader,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

type Filter = 'all' | 'unresolved' | 'inbound' | 'outbound';

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'unresolved', label: 'Needs follow-up' },
  { key: 'all', label: 'All calls' },
  { key: 'inbound', label: 'Inbound' },
  { key: 'outbound', label: 'Outbound' },
];

/**
 * The call log, opening on the follow-up queue rather than everything.
 *
 * A dispatcher's actual question first thing in the morning is "who rang while
 * we were shut and hasn't been called back", not "show me every call".
 */
export function CallsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('unresolved');
  const { connected } = useCalls();
  const { data: telephony } = useTelephony();

  const query = useMemo(() => {
    if (filter === 'unresolved') return { unresolvedOnly: true, limit: 100 };
    if (filter === 'inbound') return { direction: 'inbound' as const, limit: 100 };
    if (filter === 'outbound') return { direction: 'outbound' as const, limit: 100 };
    return { limit: 100 };
  }, [filter]);

  const { data: calls = [], isLoading } = useQuery({
    queryKey: ['calls', query],
    queryFn: () => api.calls.list(query as never),
    select: (d) => d.items,
    placeholderData: (prev) => prev,
    refetchInterval: 30_000,
  });

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Calls"
        subtitle={
          <span className="flex items-center gap-2">
            <span
              className={cx(
                'inline-block size-1.5 rounded-full',
                connected ? 'bg-good' : 'bg-ink-4',
              )}
              aria-hidden
            />
            {telephony
              ? connected
                ? `Live via ${telephony.displayName}`
                : `${telephony.displayName} — reconnecting`
              : 'No phone system configured'}
          </span>
        }
        tabs={
          <FilterTabs
            value={filter}
            onChange={setFilter}
            options={FILTERS.map((f) => ({ value: f.key, label: f.label }))}
          />
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        <div className="mx-auto max-w-[1860px]">
          <Card
            title={FILTERS.find((f) => f.key === filter)?.label ?? 'Calls'}
            icon="phone"
            count={calls.length}
            divided
            bodyClassName="p-0"
          >
            {isLoading ? (
              <div className="grid place-items-center py-16">
                <Spinner className="size-5 text-ink-4" />
              </div>
            ) : calls.length === 0 ? (
              <EmptyState
                title={filter === 'unresolved' ? 'Nobody waiting on a call back' : 'No calls yet'}
                description={
                  filter === 'unresolved'
                    ? 'Missed calls and voicemails land here until somebody notes what happened.'
                    : 'Calls appear here as soon as the phone system reports them.'
                }
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th className="w-[34%]">Caller</Th>
                    <Th>Outcome</Th>
                    <Th>When</Th>
                    <Th align="right">Follow-up</Th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => (
                    <CallRow
                      key={call.id}
                      call={call}
                      onOpenCustomer={(id) => navigate(`/customers/${id}`)}
                    />
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>

        <PageFooter label={`${calls.length} calls`} />
      </div>
    </>
  );
}

function CallRow({
  call,
  onOpenCustomer,
}: {
  call: Call;
  onOpenCustomer: (customerId: string) => void;
}) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState(false);

  const inbound = call.direction === 'inbound';
  const peer = inbound ? call.fromNumber : call.toNumber;
  const bad = call.status === 'missed' || call.status === 'failed';

  const saveNote = useMutation({
    mutationFn: () => api.calls.update(call.id, { notes: note.trim() || null }),
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['calls'] });
    },
  });

  return (
    <tr className="align-top hover:bg-raised">
      {/*
       * Who rang, in one cell: the person if we know them, the number
       * underneath. Splitting those across two columns meant the name and the
       * number it belongs to could be read in the wrong pairing at a glance.
       */}
      <Td>
        <div className="flex items-start gap-2.5">
          <span
            className={cx(
              'mt-0.5 grid size-6 shrink-0 place-items-center rounded-full',
              bad ? 'bg-danger-soft text-danger' : 'bg-accent-soft text-accent',
            )}
            aria-label={inbound ? 'Inbound' : 'Outbound'}
          >
            <Icon name="arrow" size={12} className={inbound ? 'rotate-180' : undefined} />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              {call.customer ? (
                <button
                  onClick={() => onOpenCustomer(call.customer!.id)}
                  className="text-[14px] font-semibold text-ink hover:text-accent"
                >
                  {call.customer.name}
                </button>
              ) : (
                <span className="text-[14px] font-semibold text-ink-3">Unknown caller</span>
              )}
              {call.job && (
                <Link to={`/jobs/${call.job.id}`}>
                  <Badge tone="blue">#{call.job.number}</Badge>
                </Link>
              )}
            </div>
            <div className="mt-1 text-[12px] tnum text-ink-4">{formatPhone(peer)}</div>
          </div>
        </div>
      </Td>

      <Td>
        <span className={cx('text-[13px]', bad && 'font-medium text-danger')}>
          {describeCall({
            direction: call.direction,
            status: call.status,
            durationSeconds: call.durationSeconds,
            handledByName: call.handledBy?.name ?? null,
          })}
        </span>
        {call.voicemailUrl && (
          <audio controls preload="none" src={call.voicemailUrl} className="mt-1 h-7 w-[220px]">
            <track kind="captions" />
          </audio>
        )}
      </Td>

      <Td className="tnum text-[12.5px] text-ink-3">{formatDateTime(call.startedAt)}</Td>

      <Td align="right">
        {editing ? (
          <div className="flex items-center justify-end gap-1.5">
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What happened?"
              className="h-8 w-[200px]"
              autoFocus
            />
            <Button size="sm" variant="primary" loading={saveNote.isPending} onClick={() => saveNote.mutate()}>
              Save
            </Button>
          </div>
        ) : call.notes ? (
          <button
            onClick={() => {
              setNote(call.notes ?? '');
              setEditing(true);
            }}
            className="max-w-[240px] truncate text-right text-[12.5px] text-ink-2 hover:text-accent"
            title={call.notes}
          >
            {call.notes}
          </button>
        ) : (
          <div className="flex items-center justify-end gap-1.5">
            <CallActions
              phone={peer}
              customerId={call.customerId}
              jobId={call.jobId}
              customerName={call.customer?.name}
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setNote('');
                setEditing(true);
              }}
            >
              Note
            </Button>
          </div>
        )}
      </Td>
    </tr>
  );
}
